import * as PIXI from "pixi.js";
import anime from "animejs";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";

/**
 * FeatureIntroCard — the bonus intro card, built from static images.
 *
 * It replaces the `feature_intro` spine (3.5 MB of atlas) and copies it part
 * for part: the same art in the spine's own units and the same `in` / `loop`
 * / `out` clips, keyframe for keyframe, played with animejs.
 *
 *   frame    the card background                 (spine bone `hnwintro_bg`)
 *   board    the feature's board picture         (spine bone `game_screen`)
 *   jackpot  H&W only: the Royal Jackpot badge   (spine bone `jckpt_pop`)
 *   texts    one holder per line of copy, and the tap word
 *
 * The copy (see CARD_TEXT). The owner picks the lines above and under the
 * board (setCopyLines); they stack away from it in reading order:
 *
 *   Free Spins                  Hold & Win
 *   congrats CONGRATULATIONS!   congrats CONGRATULATIONS!
 *   lead     YOU WON            lead     YOU WON
 *   count    8
 *   [board]                     [board]
 *   label    FREE SPINS         title    CLEOPATRA'S RESPINS
 *   support  the short description, under those
 *   tap      TAP TO START (on the intro screen's tap line)
 *
 * Units: spine units, origin at the card centre, PIXI y-down. The owner
 * scales `view` to the screen.
 *
 * Used by FeatureIntroController (FS and H&W intros) and by
 * AdditionalFreeSpinsController (the FS retrigger card).
 */

// The card's own background has not been delivered yet; the intro screen's
// background stands in. Drawn into the spine background's box, so the real
// asset drops in by changing this name.
// const FRAME_TEXTURE = "intro_background_portrait";
// Delivered (2026-10-12): each feature's own background, one per orientation
// (setLandscape picks). Drawn at its own aspect, covering the box below — the
// stand-in was stretched into it. The stand-in stays the fallback.
const FRAME_TEXTURE = "intro_background_portrait";
const FRAME_TEXTURES = {
  free_spins: {
    portrait: "free_spins_intro_background_portrait",
    landscape: "free_spins_intro_background_landscape",
  },
  hold_and_win: {
    portrait: "hold_and_win_intro_background_portrait",
    landscape: "hold_and_win_intro_background_landscape",
  },
};

const BOARD_REST_SCALE = 0.85;

/**
 * Per feature. `box` is the spine background attachment's box (its size and
 * centre in the frame's own units). Each picture's x / y / scale lines the
 * static art up with the spine's (measured on the opaque board frame).
 * `top` / `bottom` are the board art's edges on the card at the loop's peak
 * (1.059), so the copy never touches the pulsing board. Both boards sit 40
 * higher than the spine's, to make room for the copy under them.
 */
const LAYOUTS = {
  free_spins: {
    frame: { x: 0.5, y: 17.5, box: { x: 0, y: 63, w: 824, h: 958 } },
    board: {
      x: 0,
      y: 6.19,
      top: -225,
      bottom: 226,
      picture: { texture: "startpage_image_4", x: 1.5, y: -2.6, scale: 0.963 },
    },
    jackpot: null,
  },
  hold_and_win: {
    frame: { x: 0.5, y: 17.5, box: { x: 0, y: 0, w: 824, h: 806 } },
    board: {
      x: 0,
      y: 6.19,
      top: -360,
      bottom: 238,
      picture: { texture: "startpage_image_1", x: -3.5, y: -63, scale: 1 },
    },
    // jackpot: {
    //   x: -3,
    //   y: -95.5,
    //   picture: { texture: "paytable_royal_jackpot", x: 1.5, y: -29.8, scale: 0.975 },
    // },
    // The board alone — the Buy Bonus Hold & Win card's own picture (Vladimir,
    // 2026-10-12). The ROYAL JACKPOT badge over it went (it also read 10000x).
    jackpot: null,
  },
};

/**
 * Every line of copy has its own box (card units). A single line SHRINKS to
 * fit its box; the description WRAPS at the box width and steps its font
 * down until the block fits the box height. The authored size is the ceiling.
 *
 *   style    FONT_STYLES key
 *   size     the authored font size
 *   w, h     the box (wLandscape: the description's width in landscape)
 *
 * The description's height is a ceiling too: the owner lowers it to end
 * above the tap word (setSupportHeight).
 */
export const CARD_TEXT = {
  congrats: { style: "freeSpinsCongrats", size: 84, w: 1000, h: 92 },
  lead: { style: "freeSpinsText", size: 56, w: 900, h: 64 },
  count: { style: "winCelebrationCounter", size: 110, w: 600, h: 114 },
  label: { style: "freeSpinsText", size: 64, w: 900, h: 72 },
  title: { style: "freeSpinsCongrats", size: 84, w: 1000, h: 92 },
  support: {
    style: "tapToContinue",
    size: 40,
    w: 700,
    wLandscape: 1000,
    h: 220,
    minSize: 14,
    wrap: true,
  },
};

const COPY_LINES = ["congrats", "lead", "count", "label", "title"];

// Gaps, card units: between the board and the copy, and between copy lines.
const BOARD_GAP = 24;
const LINE_GAP = 4;
const SUPPORT_GAP = 12;

// The tap word's resting place (the spine's tap bone) and the room it has.
export const TAP_ANCHOR = { x: 0, y: 856.5, width: 400 };

/**
 * One "You won / {count} / Free Spins" sentence, split on the COUNT's line
 * (not on positions from the end: Finnish breaks the label over two lines).
 * The count's line may carry a "+" (the retrigger).
 *
 *   after it        the label, joined onto one line
 *   just before it  the lead
 *   above that      a congratulation, when the sentence has one
 *
 * @returns {{congrats: string, lead: string, label: string, found: boolean}}
 *   upper-cased; `found` = the sentence had a count line
 */
export const splitCountSentence = (sentence, count) => {
  // Trimmed: zh-CN pads its lines with a trailing space.
  const lines = String(sentence ?? "").split("\n").map((l) => l.trim());
  const countAt = lines.findIndex((l) => l === String(count) || l === `+${count}`);
  let congrats;
  let lead;
  let label;
  if (countAt >= 0) {
    label = lines.slice(countAt + 1).join(" ");
    lead = lines[countAt - 1] ?? "";
    congrats = lines.slice(0, Math.max(0, countAt - 1)).join(" ");
  } else {
    label = lines[lines.length - 1] ?? "";
    lead = lines.length > 2 ? lines[lines.length - 3] : "";
    congrats = lines.length > 3 ? lines[0] : "";
  }
  return {
    congrats: congrats.toUpperCase(),
    lead: lead.toUpperCase(),
    label: label.toUpperCase(),
    found: countAt >= 0,
  };
};

/**
 * Fit a card text into its box (see CARD_TEXT).
 *
 * @param {PIXI.Text} textObj  made by FeatureIntroCard.addText
 * @param {boolean} landscape
 */
export const fitCardText = (textObj, landscape) => {
  const box = textObj?._box;
  if (!box) return;
  const baseStyle = FONT_STYLES[box.style];
  const maxH = textObj._maxHeight ?? box.h;
  if (box.wrap) {
    const minSize = box.minSize ?? 12;
    const width = landscape && box.wLandscape ? box.wLandscape : box.w;
    const styleAt = (s) =>
      new PIXI.TextStyle(
        scaleStyle(baseStyle, s, { wordWrap: true, wordWrapWidth: width, align: "center" }),
      );
    let size = box.size;
    textObj.style = styleAt(size);
    textObj.scale.set(1);
    while (size > minSize && textObj.height > maxH) {
      size -= 2;
      textObj.style = styleAt(size);
    }
    // A single word wider than the box still has to fit.
    if (textObj.width > width) textObj.scale.set(width / textObj.width);
    return;
  }
  textObj.style = new PIXI.TextStyle(scaleStyle(baseStyle, box.size));
  textObj.scale.set(1);
  const k = Math.min(1, box.w / (textObj.width || 1), maxH / (textObj.height || 1));
  textObj.scale.set(k);
};

/**
 * The clips, from feature_intro.json. A key is [time s, value, curve] and the
 * curve is the spine's bezier [cx1, cy1, cx2, cy2] towards the next key (none
 * = linear). Values are the spine's: frameScale absolute, boardScale on top of
 * BOARD_REST_SCALE, jackpotScale inside the board. textAlpha is every text
 * slot (they all key the same fade).
 */
const CLIPS = {
  in: {
    duration: 1,
    tracks: {
      frameScale: [[0, 0.8, [0.113, 0.8, 0.22, 2.02]], [0.3333, 2.02, [0.553, 2.02, 0.78, 2]], [1, 2]],
      frameAlpha: [[0, 0, [0.111, 0, 0.222, 1]], [0.3333, 1]],
      textAlpha: [[0, 0], [0.3333, 1]],
      boardScale: [[0, 0, [0.158, 0, 0.308, 1.059]], [0.4667, 1.059, [0.643, 1.059, 0.824, 1]], [1, 1]],
      jackpotScale: [[0, 1, [0.226, 1, 0.44, 1.059]], [0.6667, 1.059, [0.777, 1.059, 0.89, 1]], [1, 1]],
    },
  },
  loop: {
    duration: 1.6667,
    tracks: {
      frameScale: [[0, 2, [0.167, 2, 0.333, 2.02]], [0.5, 2.02, [0.889, 2.02, 1.278, 2]], [1.6667, 2]],
      frameAlpha: [[0, 1]],
      textAlpha: [[0, 1]],
      boardScale: [[0, 1, [0.167, 1, 0.333, 1.059]], [0.5, 1.059, [0.889, 1.059, 1.278, 1]], [1.6667, 1]],
      jackpotScale: [[0, 1, [0.167, 1, 0.333, 1.059]], [0.5, 1.059, [0.889, 1.059, 1.278, 1]], [1.6667, 1]],
    },
  },
  out: {
    duration: 1,
    tracks: {
      frameScale: [[0, 2, [0.22, 2, 0.447, 2.02]], [0.6667, 2.02, [0.78, 2.02, 0.887, 0.8]], [1, 0.8]],
      frameAlpha: [[0.6667, 1, [0.778, 1, 0.889, 0]], [1, 0]],
      textAlpha: [[0.6667, 1], [1, 0]],
      boardScale: [[0, 1, [0.176, 1, 0.357, 1.059]], [0.5333, 1.059, [0.692, 1.059, 0.842, 0]], [1, 0]],
      jackpotScale: [[0, 1, [0.11, 1, 0.223, 1.059]], [0.3333, 1.059, [0.56, 1.059, 0.774, 1]], [1, 1]],
    },
  },
};

const clamp01 = (v) => Math.min(1, Math.max(0, v));

/** A spine bezier segment as an anime easing. */
const easingFor = (curve, t0, v0, t1, v1) => {
  if (!curve || v1 === v0) return "linear";
  const [cx1, cy1, cx2, cy2] = curve;
  const dt = t1 - t0;
  const dv = v1 - v0;
  const x1 = clamp01((cx1 - t0) / dt).toFixed(4);
  const y1 = ((cy1 - v0) / dv).toFixed(4);
  const x2 = clamp01((cx2 - t0) / dt).toFixed(4);
  const y2 = ((cy2 - v0) / dv).toFixed(4);
  return `cubicBezier(${x1}, ${y1}, ${x2}, ${y2})`;
};

/**
 * One track as anime keyframes, held at its first value until its first key
 * and at its last value until the clip ends, so every track lasts the clip.
 */
const toKeyframes = (keys, clipSeconds) => {
  const frames = [];
  let [t0, v0, curve] = keys[0];
  if (t0 > 0) frames.push({ value: v0, duration: t0 * 1000, easing: "linear" });
  for (let i = 1; i < keys.length; i++) {
    const [t1, v1, next] = keys[i];
    frames.push({ value: v1, duration: (t1 - t0) * 1000, easing: easingFor(curve, t0, v0, t1, v1) });
    [t0, v0, curve] = [t1, v1, next];
  }
  if (t0 < clipSeconds) {
    frames.push({ value: v0, duration: (clipSeconds - t0) * 1000, easing: "linear" });
  }
  return frames;
};

const sprite = (name) => {
  const texture = PIXI.Assets.cache.has(name) ? PIXI.Assets.get(name) : null;
  if (!texture) console.warn("[FeatureIntroCard] Texture not loaded:", name);
  const s = new PIXI.Sprite(texture ?? PIXI.Texture.EMPTY);
  s.anchor.set(0.5);
  return s;
};

const placePicture = (s, { x, y, scale }) => {
  s.position.set(x, y);
  s.scale.set(scale);
};

class FeatureIntroCard {
  /** @param {"free_spins"|"hold_and_win"} feature */
  constructor(feature) {
    const layout = LAYOUTS[feature] ?? LAYOUTS.free_spins;
    this._frameTextures = FRAME_TEXTURES[feature] ?? FRAME_TEXTURES.free_spins;

    this.view = new PIXI.Container();
    this.view.name = `feature_intro_card_${feature}`;
    this.view.eventMode = "passive";

    // Frame: the background, drawn into the spine attachment's box.
    this.frame = new PIXI.Container();
    this.frame.position.set(layout.frame.x, layout.frame.y);
    // The intro background is only 60-70% opaque (the intro screen draws it
    // on black), so a black copy goes under it: the game does not show
    // through, and the soft edges stay soft.
    const { box } = layout.frame;
    // const backing = sprite(FRAME_TEXTURE);
    // backing.tint = 0x000000;
    // const background = sprite(FRAME_TEXTURE);
    // for (const s of [backing, background]) {
    //   s.position.set(box.x, box.y);
    //   s.width = box.w;
    //   s.height = box.h;
    //   this.frame.addChild(s);
    // }
    // Textures and sizes set by setLandscape (portrait until told otherwise).
    const backing = new PIXI.Sprite(PIXI.Texture.EMPTY);
    backing.anchor.set(0.5);
    backing.tint = 0x000000;
    const background = new PIXI.Sprite(PIXI.Texture.EMPTY);
    background.anchor.set(0.5);
    for (const s of [backing, background]) {
      s.position.set(box.x, box.y);
      this.frame.addChild(s);
    }
    this._frameBox = box;
    this._frameSprites = [backing, background];
    this._frameName = null;
    this.setLandscape(false);

    // Board: the feature's picture, and H&W's jackpot badge inside it.
    this.board = new PIXI.Container();
    this.board.position.set(layout.board.x, layout.board.y);
    const boardPicture = sprite(layout.board.picture.texture);
    placePicture(boardPicture, layout.board.picture);
    this.board.addChild(boardPicture);

    this.jackpot = null;
    if (layout.jackpot) {
      this.jackpot = new PIXI.Container();
      this.jackpot.position.set(layout.jackpot.x, layout.jackpot.y);
      const badge = sprite(layout.jackpot.picture.texture);
      placePicture(badge, layout.jackpot.picture);
      this.jackpot.addChild(badge);
      this.board.addChild(this.jackpot);
    }

    // Texts: one holder per line, all under one layer the clips fade.
    this.texts = new PIXI.Container();
    this.texts.eventMode = "passive";
    this.holders = {};
    const addHolder = (name, y, x = 0) => {
      const holder = new PIXI.Container();
      holder.name = `feature_intro_${name}`;
      holder.position.set(x, y);
      holder.eventMode = "passive";
      this.texts.addChild(holder);
      this.holders[name] = holder;
    };
    // Placed by setCopyLines; the description's holder is its TOP.
    for (const name of [...COPY_LINES, "support"]) addHolder(name, 0);
    addHolder("tap", TAP_ANCHOR.y, TAP_ANCHOR.x);
    this._boardTop = layout.board.top;
    this._boardBottom = layout.board.bottom;
    this.setCopyLines({ above: COPY_LINES });

    this.view.addChild(this.frame, this.board, this.texts);
    this._pictures = [background, boardPicture, ...(this.jackpot ? this.jackpot.children : [])];

    this.pose = {};
    this._anim = null;
    this.setPose("in", 0);
  }

  /** The holder a text or the tap word goes into. */
  holder(name) {
    return this.holders[name] ?? null;
  }

  /**
   * The copy lines this card shows, in reading order: `above` stacks upward
   * from the board's top, `below` downward from its bottom, with even gaps;
   * the description goes under `below`. Lines in neither are hidden.
   *
   * @param {{above?: string[], below?: string[]}} lines  CARD_TEXT keys
   */
  setCopyLines({ above = [], below = [] }) {
    let edge = this._boardTop - BOARD_GAP;
    for (const name of [...above].reverse()) {
      const { h } = CARD_TEXT[name];
      this.holders[name].y = edge - h / 2;
      edge -= h + LINE_GAP;
    }
    edge = this._boardBottom + BOARD_GAP;
    for (const name of below) {
      const { h } = CARD_TEXT[name];
      this.holders[name].y = edge + h / 2;
      edge += h + LINE_GAP;
    }
    // A little air between the last line and the description.
    this.supportTop = below.length ? edge - LINE_GAP + SUPPORT_GAP : edge;
    this.holders.support.y = this.supportTop;
    const shown = new Set([...above, ...below]);
    for (const name of COPY_LINES) this.holders[name].visible = shown.has(name);
  }

  /**
   * A line of copy in its holder, fitted to its CARD_TEXT box.
   *
   * @param {"lead"|"count"|"label"|"title"|"support"} name
   * @returns {PIXI.Text|null}
   */
  addText(name, text, landscape) {
    const holder = this.holders[name];
    const box = CARD_TEXT[name];
    if (!holder || !box) return null;
    const textObj = new PIXI.Text(String(text ?? ""));
    textObj.resolution = 2;
    // The description hangs from its holder; every other line is centred on it.
    textObj.anchor.set(0.5, box.wrap ? 0 : 0.5);
    textObj._box = box;
    holder.addChild(textObj);
    fitCardText(textObj, landscape);
    return textObj;
  }

  /**
   * Cap the description so it ends `bottomY` (card units) — above the tap
   * word. Returns the height it now has.
   */
  setSupportHeight(textObj, bottomY, landscape) {
    if (!textObj?._box) return 0;
    const room = Math.max(60, Math.min(textObj._box.h, bottomY - this.supportTop));
    textObj._maxHeight = room;
    fitCardText(textObj, landscape);
    return room;
  }

  /** A clip's length, ms. */
  duration(clip) {
    return Math.round((CLIPS[clip]?.duration ?? 1) * 1000);
  }

  /** When the texts have finished fading in during `clip`, ms. */
  revealMs(clip) {
    const keys = CLIPS[clip]?.tracks.textAlpha ?? [];
    return Math.round((keys[keys.length - 1]?.[0] ?? 1) * 1000);
  }

  /** Pose the card on a clip's first (at 0) or last frame, without playing. */
  setPose(clip, at = 0) {
    const { tracks } = CLIPS[clip];
    for (const [name, keys] of Object.entries(tracks)) {
      this.pose[name] = (at === 0 ? keys[0] : keys[keys.length - 1])[1];
    }
    this._apply();
  }

  /**
   * Play a clip from its first frame.
   *
   * @param {"in"|"loop"|"out"} clip
   * @param {object}   [opts]
   * @param {boolean|number} [opts.loop]  true = forever, n = n times (default 1)
   * @param {Function} [opts.onLoop]      at the end of every pass (the seam)
   * @param {Function} [opts.onComplete]  after the last pass
   */
  play(clip, { loop = 1, onLoop, onComplete } = {}) {
    this.stop();
    const { duration, tracks } = CLIPS[clip];
    this.setPose(clip, 0);
    const props = {};
    for (const [name, keys] of Object.entries(tracks)) {
      props[name] = toKeyframes(keys, duration);
    }
    this._anim = anime({
      targets: this.pose,
      ...props,
      loop,
      update: () => this._apply(),
      loopComplete: () => {
        if (loop === true || this._anim?.remaining) onLoop?.();
      },
      complete: () => {
        this._anim = null;
        onComplete?.();
      },
    });
  }

  stop() {
    this._anim?.pause();
    this._anim = null;
  }

  /**
   * The background for the orientation (FRAME_TEXTURES), at its own aspect,
   * scaled to COVER the box — never squashed; its soft edges run past it.
   * Falls back to the stand-in (stretched, as before) if it is not loaded.
   * @param {boolean} landscape
   */
  setLandscape(landscape) {
    const wanted = this._frameTextures[landscape ? "landscape" : "portrait"];
    const name = PIXI.Assets.cache.has(wanted) ? wanted : FRAME_TEXTURE;
    if (name === this._frameName) return;
    this._frameName = name;
    if (name !== wanted) console.warn("[FeatureIntroCard] Texture not loaded:", wanted);
    const texture = PIXI.Assets.cache.has(name) ? PIXI.Assets.get(name) : PIXI.Texture.EMPTY;
    const box = this._frameBox;
    for (const s of this._frameSprites) {
      s.texture = texture;
      if (name === wanted && texture.width && texture.height) {
        s.scale.set(Math.max(box.w / texture.width, box.h / texture.height));
      } else {
        s.width = box.w;
        s.height = box.h;
      }
    }
  }

  /** Push the textures to the GPU now, not on the card's first show. */
  warmUp() {
    for (const picture of this._pictures) {
      SpineAtlasUploader.uploadTexture(picture.texture);
    }
    // Both orientations' backgrounds — a rotation must not upload one.
    for (const name of Object.values(this._frameTextures)) {
      if (PIXI.Assets.cache.has(name)) SpineAtlasUploader.uploadTexture(PIXI.Assets.get(name));
    }
  }

  _apply() {
    const p = this.pose;
    this.frame.scale.set(p.frameScale);
    this.frame.alpha = p.frameAlpha;
    this.board.scale.set(BOARD_REST_SCALE * p.boardScale);
    this.jackpot?.scale.set(p.jackpotScale);
    this.texts.alpha = p.textAlpha;
  }

  destroy() {
    this.stop();
    this.view.destroy({ children: true });
    this.holders = {};
    this._pictures = [];
  }
}

export default FeatureIntroCard;
